/**
 * FairwayBid – die Angebotsaussicht einer Anfrage
 *
 * Der Anbieter fragt sich, ob sich das Rechnen lohnt (siehe dealChance.js).
 * Die Anlage fragt sich etwas anderes: Bekomme ich auf diese Ausschreibung
 * überhaupt Angebote, die ich nebeneinanderlegen kann?
 *
 * Das ist keine Glaubensfrage. Eine Anfrage ohne Stückzahl, ohne Budgetrahmen
 * und ohne Termin zwingt jeden Anbieter zu Annahmen – und weil jeder andere
 * Annahmen trifft, sind die Preise am Ende nicht vergleichbar. Eine
 * Angebotsfrist von sieben Tagen trifft den Anbieter mitten in der Saison,
 * wo er auf dem Platz steht und nicht am Schreibtisch. Beides steht schon im
 * Formular, lange bevor es zu spät ist.
 *
 * Wo die Zahl steht – und warum dort
 * ----------------------------------
 * Beim Ausfüllen. Danach ist die Anfrage veröffentlicht, und was fehlt, fehlt
 * bereits drei Anbietern. Aus dem bisherigen Mängelkatalog ("noch 3 Felder
 * offen") wird damit eine Aussage darüber, was die Lücken kosten.
 *
 * Und im Dashboard, aber anders: Dort ist die Anfrage längst draußen, eine
 * Note nützt nichts mehr. Dort zählt nur die Frage, ob nach ein paar Tagen
 * noch nichts gekommen ist – und was sich dann noch nachtragen lässt (siehe
 * stilleAnfrage weiter unten).
 *
 * Was die Zahl nicht ist
 * ----------------------
 * Keine Vorhersage. Ob ein Anbieter antwortet, hängt an seinem Kalender,
 * seinem Lager und daran, wie viele Anfragen gerade offen sind – davon wissen
 * wir nichts. Sie misst, ob die Anfrage ein Anbieter beantworten KANN, ohne
 * zurückzufragen. Das ist weniger, als eine Prozentzahl verspricht, und es
 * ist das Einzige, was an dieser Stelle ehrlich zu haben ist.
 *
 * Später kommt eine echte Zahl dazu: wie viele registrierte Anbieter zu
 * dieser Kategorie und diesem Lieferland passen. Die steht in unseren Daten,
 * ist aber nur über die Integration API zu zählen. Deshalb ist sie hier als
 * Zusatz vorgesehen (mitAnbieterzahl) und nicht in die Punkte eingerechnet –
 * eine Zahl, die manchmal da ist und manchmal nicht, dürfte den Wert nicht
 * schwanken lassen.
 */

import { QUOTE_DEADLINE_DAYS, getDeadlineStatus } from './deadlines';
import { mengenfeldFuer } from './quantityField';
import { BUNDLE_OPEN_VALUES } from './bundles';

const MIN_BESCHREIBUNG = 220;

/** Was zählt und wie viel. Zusammen 100. */
export const AUSSICHT_GEWICHT = {
  beschreibung: 20,
  menge: 15,
  budget: 20,
  termin: 10,
  frist: 20,
  leistungen: 15,
};

export const AUSSICHT_STUFEN = [
  { ab: 75, name: 'hoch', text: 'Gute Aussicht auf vergleichbare Angebote' },
  { ab: 50, name: 'mittel', text: 'Brauchbar – ein paar Angaben fehlen' },
  { ab: 0, name: 'gering', text: 'So bekommen Sie kaum vergleichbare Angebote' },
];

export const aussichtStufe = wert =>
  AUSSICHT_STUFEN.find(s => wert >= s.ab) || AUSSICHT_STUFEN[AUSSICHT_STUFEN.length - 1];

const hatZahl = text => /\d/.test(text || '');
const alsListe = wert => (Array.isArray(wert) ? wert.filter(Boolean) : wert ? [wert] : []);

/**
 * Die Angaben einer Anfrage, unabhängig davon, wo sie herkommen.
 *
 * Formular und veröffentlichte Anzeige tragen dieselben Werte unter
 * verschiedenen Namen – im Formular mit dem Präfix pub_, in der Anzeige
 * blank. Beides hier einmal auf dieselbe Form zu bringen ist der Grund,
 * warum die Bewertung darunter nur einmal existiert: Sonst bewertete das
 * Formular anders als das Dashboard, und niemand wüsste, welche der beiden
 * Zahlen gilt.
 */
export const angabenAusFormular = (values = {}) => {
  const v = values || {};
  const kategorie = v.categoryLevel1 || v.categoryLevel2 || v.category || null;
  return {
    beschreibung: (v.description || '').trim(),
    menge: v[`pub_${mengenfeldFuer(kategorie)}`],
    budget: v.pub_budget_range,
    termin: v.pub_delivery_date,
    frist: v.pub_quote_deadline,
    leistungen: alsListe(v.pub_requested_services),
    buendel: BUNDLE_OPEN_VALUES.includes(v.pub_bundle_allowed),
    bilder: alsListe(v.images).length,
  };
};

export const angabenAusAnfrage = listing => {
  const a = listing?.attributes || {};
  const pd = a.publicData || {};
  const kategorie = pd.categoryLevel1 || pd.categoryLevel2 || pd.category || null;
  return {
    beschreibung: (a.description || '').trim(),
    menge: pd[mengenfeldFuer(kategorie)],
    budget: pd.budget_range,
    termin: pd.delivery_date,
    frist: pd.quote_deadline,
    leistungen: alsListe(pd.requested_services),
    buendel: BUNDLE_OPEN_VALUES.includes(pd.bundle_allowed),
    bilder: (listing?.images || []).length,
  };
};

const gefuellt = wert => wert !== null && wert !== undefined && String(wert).trim() !== '';

/**
 * Die Aussicht aus einem Satz Angaben.
 *
 * @returns {{wert:number, stufe:Object, gruende:Array, fehlend:Array<string>}}
 */
export const aussichtAus = angaben => {
  const a = angaben || {};
  const gruende = [];
  const fehlend = [];
  let punkte = 0;

  // --- Beschreibung ---
  const b = a.beschreibung || '';
  if (b.length >= MIN_BESCHREIBUNG && hatZahl(b)) {
    punkte += AUSSICHT_GEWICHT.beschreibung;
    gruende.push({ gut: true, text: 'Die Beschreibung ist ausführlich und nennt Zahlen' });
  } else if (b.length === 0) {
    fehlend.push('Beschreibung');
    gruende.push({ gut: false, text: 'Ohne Beschreibung weiß niemand, worum es geht' });
  } else {
    punkte += Math.round(AUSSICHT_GEWICHT.beschreibung / 2);
    fehlend.push('Beschreibung');
    gruende.push({
      gut: false,
      text: hatZahl(b)
        ? 'Die Beschreibung ist knapp – Anbieter kalkulieren dann vorsichtig'
        : 'In der Beschreibung steht keine Zahl – Menge, Fläche oder Meter fehlen',
    });
  }

  // --- Stückzahl ---
  if (gefuellt(a.menge)) {
    punkte += AUSSICHT_GEWICHT.menge;
    gruende.push({ gut: true, text: 'Die Stückzahl steht als Zahl in der Anfrage' });
  } else {
    fehlend.push('Stückzahl');
    gruende.push({ gut: false, text: 'Ohne Stückzahl kann niemand staffeln oder rechnen' });
  }

  // --- Budget ---
  if (gefuellt(a.budget)) {
    punkte += AUSSICHT_GEWICHT.budget;
    gruende.push({ gut: true, text: 'Ein Budgetrahmen ist genannt' });
  } else {
    fehlend.push('Budgetrahmen');
    gruende.push({
      gut: false,
      text: 'Ohne Budgetrahmen kommen Angebote aus allen Preisklassen',
    });
  }

  // --- Wunschtermin ---
  if (gefuellt(a.termin)) {
    punkte += AUSSICHT_GEWICHT.termin;
    gruende.push({ gut: true, text: 'Der Wunschtermin steht fest' });
  } else {
    fehlend.push('Wunschtermin');
    gruende.push({ gut: false, text: 'Ohne Wunschtermin weiß kein Anbieter, ob er liefern kann' });
  }

  // --- Angebotsfrist ---
  const tage = QUOTE_DEADLINE_DAYS[a.frist];
  if (!tage) {
    fehlend.push('Angebotsfrist');
    gruende.push({ gut: false, text: 'Ohne Frist bleibt die Anfrage liegen' });
  } else if (tage <= 7) {
    punkte += Math.round(AUSSICHT_GEWICHT.frist / 2);
    gruende.push({
      gut: null,
      text: `${tage} Tage sind knapp – in der Saison steht mancher Anbieter auf dem Platz, nicht am Schreibtisch`,
    });
  } else {
    punkte += AUSSICHT_GEWICHT.frist;
    gruende.push({ gut: true, text: `${tage} Tage Angebotsfrist – genug Zeit zum Rechnen` });
  }

  // --- Leistungen ---
  if (a.leistungen && a.leistungen.length > 0) {
    punkte += AUSSICHT_GEWICHT.leistungen;
    gruende.push({ gut: true, text: 'Die gewünschten Leistungen sind benannt' });
  } else {
    fehlend.push('Leistungen');
    gruende.push({
      gut: false,
      text: 'Ohne Leistungen rechnet einer die Montage ein und der nächste nicht',
    });
  }

  // --- Zwei Angaben ohne Punkte, aber mit Wirkung ---
  if (a.buendel) {
    gruende.push({ gut: true, text: 'Für Bündelung freigegeben – die Menge kann noch wachsen' });
  }
  if (a.bilder > 0) {
    gruende.push({
      gut: true,
      text: `${a.bilder} ${a.bilder === 1 ? 'Foto' : 'Fotos'} der Gegebenheiten vor Ort`,
    });
  }

  const wert = Math.max(0, Math.min(100, Math.round(punkte)));
  return { wert, stufe: aussichtStufe(wert), gruende, fehlend };
};

/** Die Aussicht aus Formularwerten – für das Anfrageformular. */
export const aussichtFuerFormular = values => aussichtAus(angabenAusFormular(values));

/** Die Aussicht aus einer veröffentlichten Anfrage. */
export const aussichtFuerAnfrage = listing => aussichtAus(angabenAusAnfrage(listing));

/**
 * Ergänzt die Zahl passender Anbieter, sobald wir sie zählen können.
 *
 * Bewusst ohne Einfluss auf den Wert: Eine Angabe, die manchmal da ist,
 * darf die Note nicht schwanken lassen.
 */
export const mitAnbieterzahl = (aussicht, anzahl) => {
  if (!aussicht || typeof anzahl !== 'number') return aussicht;
  return {
    ...aussicht,
    anbieter: anzahl,
    gruende: [
      ...aussicht.gruende,
      {
        gut: anzahl > 0,
        text:
          anzahl === 0
            ? 'Für diese Kategorie und dieses Lieferland ist noch kein Anbieter registriert'
            : `${anzahl} ${
                anzahl === 1 ? 'Anbieter passt' : 'Anbieter passen'
              } zu Kategorie und Lieferland`,
      },
    ],
  };
};

/* ------------------------------------------------------------------ *
 * Punkt 2: die stille Anfrage
 * ------------------------------------------------------------------ */

/** Ab wann Schweigen auffällt. Vorher ist es einfach noch früh. */
export const STILL_AB_TAGEN = 4;

const MS_PRO_TAG = 24 * 60 * 60 * 1000;

/**
 * Eine veröffentlichte Anfrage, auf die nach einigen Tagen nichts kommt.
 *
 * Der Unterschied zur Aussicht oben: Hier ist keine Note gefragt, sondern
 * ein Anstoß im letzten Moment, in dem er noch etwas ändert. Wer am
 * sechsten von einundzwanzig Tagen erfährt, dass sein Budgetrahmen fehlt,
 * kann ihn nachtragen; wer es am einundzwanzigsten erfährt, kann nur noch
 * eine neue Anfrage schreiben.
 *
 * Deshalb die Schwelle: Vor dem vierten Tag ist Stille kein Signal, sondern
 * der Normalfall – Anbieter sammeln Anfragen und rechnen sie am Stück.
 *
 * @param {Object} listing die eigene Anfrage
 * @param {Array} offers die Angebote dazu
 * @param {Date} [now]
 * @returns {Object|null} null, solange es nichts zu sagen gibt
 */
export const stilleAnfrage = (listing, offers = [], now = new Date()) => {
  const a = listing?.attributes;
  if (!a || a.state !== 'published') return null;
  if ((Array.isArray(offers) ? offers.length : 0) > 0) return null;

  const start = a.publishedAt || a.createdAt;
  const seit = start ? Math.floor((now.getTime() - new Date(start).getTime()) / MS_PRO_TAG) : 0;
  if (!Number.isFinite(seit) || seit < STILL_AB_TAGEN) return null;

  const frist = getDeadlineStatus(listing, now);
  // Abgelaufen ist kein Anstoß mehr, sondern ein Ergebnis. Dafür steht an
  // der Anfrage schon das Fristetikett.
  if (frist?.expired) return null;

  const aussicht = aussichtFuerAnfrage(listing);
  const angaben = angabenAusAnfrage(listing);

  // Was sich jetzt noch nachtragen lässt – in der Reihenfolge der Wirkung.
  const vorschlaege = [];
  if (!gefuellt(angaben.budget)) {
    vorschlaege.push(
      'Budgetrahmen nachtragen – Anbieter überspringen Anfragen, bei denen sie die Preisklasse raten müssten.'
    );
  }
  if (!gefuellt(angaben.menge)) {
    vorschlaege.push('Stückzahl eintragen – ohne Zahl kann niemand einen Preis nennen, der hält.');
  }
  if (angaben.leistungen.length === 0) {
    vorschlaege.push(
      'Gewünschte Leistungen ankreuzen – Montage und Wartung entscheiden oft über den Aufwand.'
    );
  }
  if ((angaben.beschreibung || '').length < MIN_BESCHREIBUNG) {
    vorschlaege.push('Beschreibung ergänzen: Bestand, Fläche, Anschlüsse, was ersetzt wird.');
  }
  if (!angaben.buendel) {
    vorschlaege.push(
      'Für Bündelung freigeben – zusammen mit anderen Anlagen wird aus Ihrer Menge eine, für die sich das Rechnen lohnt.'
    );
  }
  if (frist && frist.daysLeft <= 5) {
    vorschlaege.push('Frist verlängern – fünf Tage reichen vielen Anbietern in der Saison nicht.');
  }

  return {
    tage: seit,
    restTage: frist ? frist.daysLeft : null,
    aussicht,
    vorschlaege: vorschlaege.slice(0, 3),
    // Alles ausgefüllt und trotzdem nichts: Dann liegt es nicht an der
    // Anfrage, und das zu sagen ist ehrlicher, als eine Aufgabe zu erfinden.
    nichtsZuTun: vorschlaege.length === 0,
  };
};
